import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Link from '@atlaskit/link';

import type { FlagsStateContainer } from '@confluence/flags';

import { ShortcutVisualizer } from './ShortcutVisualizer';

const i18n = defineMessages({
	deprecatedShortcutFlagTitle: {
		id: 'shortcuts.deprecated-shortcut-flag.title',
		defaultMessage: 'This shortcut is about to change',
		description:
			'Title of a flag that displays when the user activates a keyboard shortcut that will be removed in the future.',
	},
	deprecatedShortcutFlagDescription: {
		id: 'shortcuts.deprecated-shortcut-flag.description',
		defaultMessage:
			'Shortcuts are switching to a <supportLink>universal format</supportLink>. <inline>{oldShortcut} will now be {newShortcut}</inline>',
		description:
			'Description of a flag that displays when the user activates a keyboard shrotcut that will be removed soon.  {oldShortcut} and {newShortcut} will be representations of the keyboard shortcuts.  For example, "A" or "Cmd S".',
	},
});

const shortcutSentenceCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.025'),
});
const getShortcutDescription = ({ oldShortcut, newShortcut }) => {
	const oldS = Array.isArray(oldShortcut) ? oldShortcut[0] : oldShortcut;
	const newS = Array.isArray(newShortcut) ? newShortcut[0] : newShortcut;

	return (
		<FormattedMessage
			{...i18n.deprecatedShortcutFlagDescription}
			values={{
				supportLink: (chunks) => (
					// TODO Update the link below when support documentation is ready.
					<Link target="_blank" href="https://www.atlassian.com/">
						{chunks}
					</Link>
				),
				oldShortcut: <ShortcutVisualizer shortcut={oldS} />,
				newShortcut: <ShortcutVisualizer shortcut={newS} />,
				inline: (chunks) => <div css={shortcutSentenceCss}>{chunks}</div>,
			}}
		/>
	);
};

type MaybeShowDeprecatedShortcutFlagProps = {
	accelerator: string | string[];
	deprecatedAccelerator: string | string[];
	flags: FlagsStateContainer;
};
export const maybeShowDeprecatedShortcutFlag = ({
	flags,
	accelerator,
	deprecatedAccelerator,
}: MaybeShowDeprecatedShortcutFlagProps) => {
	// TODO MODES-6491 Add logic here to enable hiding the flag.
	void flags.showInfoFlag({
		title: <FormattedMessage {...i18n.deprecatedShortcutFlagTitle} />,
		description: getShortcutDescription({
			oldShortcut: deprecatedAccelerator,
			newShortcut: accelerator,
		}),
		isAutoDismiss: false,
	});
};
